/**
 * Seller Portal -> Invoices — /app/seller/billing/invoices (SB26).
 * Real Invoice rows from GET /api/billing/invoices (backend/app/api/
 * billing/invoices.py) — every row was generated by an actual
 * subscription lifecycle event (create/trial/renew/upgrade/downgrade/
 * add-module/extend). Detail modal fetches real line items + offers a
 * real PDF download and a real signed payment-link generation action.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, Eye, Link2, Loader2, MoreVertical, Receipt, X } from 'lucide-react';
import { Button, DataTable, EmptyState, Modal, StatusBadge } from '@/components/common/index.js';
import { PAGINATION } from '@/utils/constants.js';
import { formatDate, formatDateTime } from '@/utils/formatters.js';
import { billingInvoicesApi } from '@/services/api/seller/billingInvoicesApi.js';
import './seller-billing.css';

const STATUS_VARIANT = {
  PAID: 'success', NO_PAYMENT_REQUIRED: 'success',
  PAYMENT_PENDING: 'warning', PARTIALLY_PAID: 'warning',
  OVERDUE: 'danger', VOID: 'gray', CANCELLED: 'gray', DRAFT: 'gray', ISSUED: 'info',
};

function fmtCurrency(n, currency = 'USD') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 2 }).format(n || 0);
}
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

const STATUS_OPTIONS = ['All', 'PAID', 'PAYMENT_PENDING', 'PARTIALLY_PAID', 'OVERDUE', 'NO_PAYMENT_REQUIRED', 'VOID', 'CANCELLED'];

export default function SellerInvoices() {
  const [status, setStatus] = useState('All');
  const [page, setPage] = useState(PAGINATION.DEFAULT_PAGE);
  const [pageSize] = useState(PAGINATION.DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [linkResult, setLinkResult] = useState(null);
  const [linkError, setLinkError] = useState(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    billingInvoicesApi.listInvoices({ status: status === 'All' ? undefined : status, page, page_size: pageSize })
      .then((d) => { setRows(d.items); setTotal(d.total); setError(null); })
      .catch((err) => setError(err?.response?.data?.detail || 'Could not load invoices.'))
      .finally(() => setLoading(false));
  }, [status, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  function openDetail(row) {
    setDetailId(row.id);
    setDetail(null);
    setLinkResult(null);
    setLinkError(null);
    setDetailLoading(true);
    billingInvoicesApi.getInvoice(row.id).then(setDetail).finally(() => setDetailLoading(false));
  }

  async function handleDownloadPdf(id, invoiceNumber) {
    setPdfBusy(true);
    try {
      const blob = await billingInvoicesApi.downloadInvoicePdf(id);
      downloadBlob(blob, `${invoiceNumber}.pdf`);
    } finally {
      setPdfBusy(false);
    }
  }

  async function handleGenerateLink(id) {
    setLinkBusy(true);
    setLinkError(null);
    try {
      const result = await billingInvoicesApi.createPaymentLink(id);
      setLinkResult(result);
      return result;
    } catch (err) {
      setLinkError(err?.response?.data?.detail || 'Could not generate a payment link.');
      return null;
    } finally {
      setLinkBusy(false);
    }
  }

  const [payBusyRow, setPayBusyRow] = useState(null);

  /** Row-level "Pay Now" — generates the link and opens it directly,
   * skipping the detail modal, for the common case of just wanting to
   * pay an outstanding invoice. The tab opens SYNCHRONOUSLY on the click,
   * before the await, and gets redirected once the real URL comes back —
   * a window.open() issued after an async gap is no longer "user-
   * initiated" to most browsers' popup blockers and gets silently blocked.
   * IMPORTANT: no 'noopener'/'noreferrer' — those flags make window.open()
   * return null in every modern browser (found live: click did nothing),
   * which kills this redirect entirely. We navigate the tab to our own
   * /pay/{token} URL ourselves, so keeping the handle is safe. If a popup
   * blocker still eats the new tab (tab is null), fall back to navigating
   * THIS tab there instead of just alerting a raw URL — the payment screen
   * must actually show up either way. */
  async function handlePayNowRow(row) {
    const tab = window.open('', '_blank');
    setPayBusyRow(row.id);
    try {
      const result = await billingInvoicesApi.createPaymentLink(row.id);
      if (tab) tab.location.href = result.payment_url;
      else window.location.href = result.payment_url;
    } catch (err) {
      if (tab) tab.close();
      window.alert(err?.response?.data?.detail || 'Could not generate a payment link.');
    } finally {
      setPayBusyRow(null);
    }
  }

  const isPayable = (statusValue) => statusValue !== 'PAID' && statusValue !== 'NO_PAYMENT_REQUIRED' && statusValue !== 'VOID' && statusValue !== 'CANCELLED';

  const columns = [
    { key: 'invoice_number', header: 'Invoice #', sortable: true },
    { key: 'organization_name', header: 'Customer', sortable: true },
    { key: 'reason', header: 'Reason' },
    { key: 'status', header: 'Status', render: (v) => <StatusBadge variant={STATUS_VARIANT[v] || 'gray'} dot>{v.replace(/_/g, ' ')}</StatusBadge> },
    { key: 'total', header: 'Total', render: (v, row) => fmtCurrency(v, row.currency) },
    { key: 'issue_date', header: 'Issued', render: (v) => formatDate(v) },
    { key: 'due_date', header: 'Due', render: (v) => (v ? formatDate(v) : '—') },
    {
      key: 'actions', header: 'Actions',
      render: (_v, row) => (
        <div className="sb-row-actions" onClick={(e) => e.stopPropagation()}>
          <button className="sb-icon-btn" title="View" onClick={() => openDetail(row)}><Eye size={14} /></button>
          <InvoiceRowMenu
            onView={() => openDetail(row)}
            onPay={isPayable(row.status) ? () => handlePayNowRow(row) : null}
            payBusy={payBusyRow === row.id}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="sb-page">
      <div className="sb-header">
        <h1>Invoices</h1>
        <p className="sb-subtitle">Every invoice generated across every customer's subscription lifecycle.</p>
      </div>

      <div className="sb-filters">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s}
            className={`sb-filter-chip${status === s ? ' active' : ''}`}
            onClick={() => { setStatus(s); setPage(1); }}
          >
            {s === 'All' ? 'All' : s.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        error={error}
        onRowClick={openDetail}
        emptyState={<EmptyState icon={<Receipt size={26} />} title="No invoices" description="Invoices generated by subscription lifecycle events will appear here." />}
        pagination={{ page, pageSize, total, onChange: setPage }}
      />

      {detailId && (
        <Modal open onClose={() => setDetailId(null)} title="Invoice" size="lg">
          {detailLoading || !detail ? (
            <div className="sb-modal-loading"><Loader2 size={18} className="sb-spin" /> Loading…</div>
          ) : (
            <div className="sb-invoice-detail">
              <div className="sb-invoice-detail-head">
                <div>
                  <div className="sb-invoice-number">{detail.invoice_number}</div>
                  <div className="sb-invoice-org">{detail.organization_name}</div>
                </div>
                <StatusBadge variant={STATUS_VARIANT[detail.status] || 'gray'} dot>{detail.status.replace(/_/g, ' ')}</StatusBadge>
              </div>

              <div className="sb-invoice-meta-grid">
                <div><span>Reason</span><strong>{detail.reason}</strong></div>
                <div><span>Issued</span><strong>{formatDate(detail.issue_date)}</strong></div>
                <div><span>Due</span><strong>{detail.due_date ? formatDate(detail.due_date) : '—'}</strong></div>
                <div><span>Paid</span><strong>{detail.paid_at ? formatDateTime(detail.paid_at) : '—'}</strong></div>
              </div>

              <table className="sb-line-items">
                <thead><tr><th>Item</th><th>Qty</th><th>Unit Price</th><th>Total</th></tr></thead>
                <tbody>
                  {detail.line_items.map((li) => (
                    <tr key={li.id}>
                      <td>{li.description}</td>
                      <td>{li.quantity}</td>
                      <td>{fmtCurrency(li.unit_price, detail.currency)}</td>
                      <td>{fmtCurrency(li.total, detail.currency)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr><td colSpan={3}>Subtotal</td><td>{fmtCurrency(detail.subtotal, detail.currency)}</td></tr>
                  {detail.tax > 0 && <tr><td colSpan={3}>Tax</td><td>{fmtCurrency(detail.tax, detail.currency)}</td></tr>}
                  <tr className="sb-line-items-total"><td colSpan={3}>Total</td><td>{fmtCurrency(detail.total, detail.currency)}</td></tr>
                </tfoot>
              </table>

              <div className="sb-invoice-actions">
                <Button variant="secondary" icon={<Download size={14} />} onClick={() => handleDownloadPdf(detail.id, detail.invoice_number)} disabled={pdfBusy}>
                  {pdfBusy ? 'Downloading…' : 'Download PDF'}
                </Button>
                {detail.status !== 'PAID' && detail.status !== 'NO_PAYMENT_REQUIRED' && (
                  <Button variant="primary" icon={<Link2 size={14} />} onClick={() => handleGenerateLink(detail.id)} disabled={linkBusy}>
                    {linkBusy ? 'Generating…' : 'Generate Payment Link'}
                  </Button>
                )}
              </div>

              {linkError && <div className="sb-inline-error">{linkError}</div>}
              {linkResult && (
                <div className="sb-payment-link-result">
                  <span>Payment link (expires {formatDateTime(linkResult.expires_at)}):</span>
                  <code>{linkResult.payment_url}</code>
                </div>
              )}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

// ── Row kebab menu ───────────────────────────────────────────────

function InvoiceRowMenu({ onView, onPay, payBusy }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const btnRef = useRef(null);

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 4, left: r.right - 180 });
    }
    setOpen((o) => !o);
  };

  const act = (fn) => () => { setOpen(false); fn(); };

  return (
    <div className="sb-row-menu">
      <button ref={btnRef} className="sb-icon-btn" title="More actions" onClick={toggle}><MoreVertical size={14} /></button>
      {open && coords && createPortal(
        <>
          <div className="sb-row-menu-backdrop" onClick={() => setOpen(false)} />
          <div className="sb-row-menu-list" style={{ position: 'fixed', top: coords.top, left: coords.left }}>
            {onPay && (
              <button className="sb-row-menu-pay" disabled={payBusy} onClick={act(onPay)}>
                <Link2 size={13} /> {payBusy ? 'Opening…' : 'Pay Now'}
              </button>
            )}
            <button onClick={act(onView)}><Eye size={13} /> View</button>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
